import { ref } from "vue"

export const useEmailParts = async () => {
    const parts = ref<EmailPartT[]>([])
    const loading = ref(true)
    const error = ref<string | null>(null)

    // Fetch all email parts
    const fetchEmailParts = async () => {
        try {
            loading.value = true
            error.value = null
            const data = await $fetch("/api/v1/email-parts")
            parts.value = data
        } catch (err) {
            console.error("Error fetching email parts:", err)
            error.value = err instanceof Error ? err.message : "Failed to fetch email parts"
        } finally {
            loading.value = false
        }
    }

    // Get a single email part by alias
    const getEmailPart = async (alias: string) => {
        try {
            loading.value = true
            error.value = null
            const part = await $fetch(`/api/v1/email-parts/${alias}`)
            // withSupabase catches anything thrown inside a handler — including the
            // route's own createError(404) — and returns `{data:null, error}` with a
            // 200. So a missing part arrives here as a resolved, truthy object rather
            // than a rejection. Normalize it to null so callers' fallbacks work.
            if (part && typeof part === "object" && "error" in part) {
                console.error(`Email part '${alias}' not available:`, (part as { error: unknown }).error)
                return null
            }
            return part
        } catch (err) {
            console.error(`Error fetching email part '${alias}':`, err)
            error.value = err instanceof Error ? err.message : `Failed to fetch email part '${alias}'`
            return null
        } finally {
            loading.value = false
        }
    }

    // Save an email part
    const saveEmailPart = async (part: EmailPartT) => {
        try {
            loading.value = true
            error.value = null
            const savedPart = await $fetch(`/api/v1/email-parts/${part.alias}`, {
                method: "PUT",
                body: part
            })

            // Update the parts list if this part already exists
            const index = parts.value.findIndex(p => p.alias === part.alias)
            if (index >= 0) {
                parts.value[index] = savedPart
            } else {
                parts.value.push(savedPart)
            }

            return savedPart
        } catch (err) {
            console.error(`Error saving email part '${part.alias}':`, err)
            error.value = err instanceof Error ? err.message : `Failed to save email part '${part.alias}'`
            throw err
        } finally {
            loading.value = false
        }
    }

    // Delete an email part
    const deleteEmailPart = async (alias: string) => {
        try {
            loading.value = true
            error.value = null
            await $fetch(`/api/v1/email-parts/${alias}`, {
                method: "DELETE"
            })

            // Remove from the parts list
            parts.value = parts.value.filter(p => p.alias !== alias)

            return true
        } catch (err) {
            console.error(`Error deleting email part '${alias}':`, err)
            error.value = err instanceof Error ? err.message : `Failed to delete email part '${alias}'`
            throw err
        } finally {
            loading.value = false
        }
    }

    await fetchEmailParts()

    return {
        parts,
        loading,
        error,
        fetchEmailParts,
        getEmailPart,
        saveEmailPart,
        deleteEmailPart
    }
}
